import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { log } from '../lib/logger';
import { useAuth } from '../lib/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Plus, Trash2, Save, RefreshCcw, ChevronLeft, Calendar, DollarSign, Info,
  Copy, ArrowUpDown, Crown,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// Contract Rate Editor  (Super User only)
//
// Rates are effective-dated. Multiple rows per payer are allowed —
// the lookup engine picks the most recent row whose effective_date ≤ DOS.
// When you raise a payer's rate, DON'T edit the existing row — click
// "New rate for this payer" to add a new row with the effective date of
// the change. Historical months keep the old rate; new visits use the new.
// ─────────────────────────────────────────────────────────────

const C = {
  bg: '#FBF8F4', ink: '#685B72', ink2: '#685B72', soft: '#685B72',
  border: '#E9E3DA', hair: '#B08A3A', gold: '#B08A3A', purple: '#685B72',
  teal: '#839E97', rose: '#8A3A3A', amber: '#B08A3A',
};

const CODE_LABELS = {
  r523: '92523 · Evaluation',
  r507: '92507 · Treatment (default fallback)',
  r609: '92609 · AAC',
};

function today() { return new Date().toISOString().slice(0, 10); }

function emptyRate() {
  return {
    payer: '',
    keywords: [],
    r523: 0, r507: 0, r609: 0,
    effective_date: today(),
    notes: '',
  };
}

function DateChip({ date, latest }) {
  const tone = latest ? C.teal : C.soft;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${tone}15`, color: tone }}>
      <Calendar size={10} strokeWidth={2.4} /> {date}
      {latest && <span className="text-[9px]">· CURRENT</span>}
    </span>
  );
}

function RateRow({ row, isLatestForPayer, onChange, onDelete, onDuplicate }) {
  const bump = (patch) => onChange({ ...row, ...patch });
  const kwStr = (row.keywords || []).join(', ');
  return (
    <tr className="border-t align-top" style={{ borderColor: C.hair }} data-testid={`rate-row-${row._key}`}>
      <td className="py-2 px-3">
        <Input
          data-testid={`rate-payer-${row._key}`}
          value={row.payer}
          onChange={e => bump({ payer: e.target.value })}
          placeholder="e.g. BCBS TX"
          className="text-[13px] h-8"
        />
        <Input
          data-testid={`rate-kw-${row._key}`}
          value={kwStr}
          onChange={e => bump({ keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
          placeholder="keywords (comma-separated, e.g. BCBS, BLUE CROSS)"
          className="mt-1 text-[11px] h-7 font-mono"
          style={{ color: C.ink2 }}
        />
      </td>
      <td className="py-2 px-3 w-[110px]">
        <Input
          data-testid={`rate-eff-${row._key}`}
          type="date"
          value={row.effective_date || ''}
          onChange={e => bump({ effective_date: e.target.value })}
          className="text-[12px] h-8"
        />
        <div className="mt-1"><DateChip date={row.effective_date || '—'} latest={isLatestForPayer} /></div>
      </td>
      {['r523', 'r507', 'r609'].map(k => (
        <td key={k} className="py-2 px-2 w-[110px]">
          <div className="relative">
            <DollarSign size={11} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: C.soft }} />
            <Input
              data-testid={`rate-${k}-${row._key}`}
              type="number"
              step="0.01"
              value={row[k] ?? 0}
              onChange={e => bump({ [k]: parseFloat(e.target.value || 0) })}
              className="pl-6 text-[13px] h-8 tabular-nums text-right"
            />
          </div>
        </td>
      ))}
      <td className="py-2 px-3">
        <Input
          data-testid={`rate-notes-${row._key}`}
          value={row.notes || ''}
          onChange={e => bump({ notes: e.target.value })}
          placeholder="e.g. 2026 contract update"
          className="text-[12px] h-8"
        />
      </td>
      <td className="py-2 px-2 w-[90px] text-right">
        <div className="flex gap-1 justify-end">
          <Button data-testid={`rate-dup-${row._key}`} variant="ghost" size="sm" onClick={() => onDuplicate(row)}
                  className="h-7 w-7 p-0" title="Duplicate as new effective row">
            <Copy size={13} style={{ color: C.purple }} />
          </Button>
          <Button data-testid={`rate-del-${row._key}`} variant="ghost" size="sm" onClick={() => onDelete(row)}
                  className="h-7 w-7 p-0" title="Delete row">
            <Trash2 size={13} style={{ color: C.rose }} />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export default function RateEditor() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sortBy, setSortBy] = useState('payer_asc'); // payer_asc | eff_desc

  const isForbidden = user && user.role !== 'super_user';

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/bi/rates');
      const rates = (r.data?.rates || []).map((rate, i) => ({ ...rate, _key: `k${i}_${Math.random().toString(36).slice(2, 7)}` }));
      setRows(rates);
      setDirty(false);
    } catch (e) { toast.error('Failed to load rates'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (!isForbidden) load(); }, [isForbidden]);

  const updateRow = (updated) => {
    setRows(rs => rs.map(r => (r._key === updated._key ? updated : r)));
    setDirty(true);
  };
  const deleteRow = (row) => {
    if (!window.confirm(`Delete rate row for "${row.payer}" (${row.effective_date})?`)) return;
    setRows(rs => rs.filter(r => r._key !== row._key));
    setDirty(true);
  };
  const duplicateRow = (row) => {
    const clone = { ...row, effective_date: today(), notes: row.notes ? `${row.notes} (new rate)` : 'new rate',
                    _key: `k_${Math.random().toString(36).slice(2, 8)}` };
    setRows(rs => [clone, ...rs]);
    setDirty(true);
    toast.info(`Cloned "${row.payer}" — set the new effective date and update the values.`);
  };
  const addRow = () => {
    setRows(rs => [{ ...emptyRate(), _key: `k_${Math.random().toString(36).slice(2, 8)}` }, ...rs]);
    setDirty(true);
  };

  const save = async () => {
    // Validate rows
    for (const r of rows) {
      if (!r.payer?.trim()) { toast.error('Every row needs a payer name.'); return; }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(r.effective_date || '')) {
        toast.error(`"${r.payer}" is missing a valid effective date (YYYY-MM-DD).`); return;
      }
    }
    setSaving(true);
    try {
      // Strip internal _key field before send
      const payload = { rates: rows.map(({ _key, ...rest }) => rest) };
      await api.put('/bi/rates', payload);
      toast.success(`Saved ${rows.length} rate rows.`);
      setDirty(false);
      // Trigger a refresh so the new rates take effect
      try { await api.post('/bi/refresh'); toast.info('BI cube refreshed with new rates.'); }
      catch (err) {
        // Rates saved, cube refresh optional — user can trigger manually.
        log.warn('BI cube refresh after rate save failed:', err);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  // Compute which row is the current/latest for each payer (highest effective_date ≤ today)
  const latestKeyByPayer = useMemo(() => {
    const t = today();
    const byPayer = {};
    for (const r of rows) {
      const eff = r.effective_date || '2020-01-01';
      if (eff > t) continue;
      const cur = byPayer[r.payer];
      if (!cur || (cur.effective_date || '') < eff) byPayer[r.payer] = r;
    }
    return new Set(Object.values(byPayer).map(r => r._key));
  }, [rows]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      if (sortBy === 'eff_desc') return (b.effective_date || '').localeCompare(a.effective_date || '');
      // payer_asc, secondary by effective_date desc
      const p = (a.payer || '').localeCompare(b.payer || '');
      if (p !== 0) return p;
      return (b.effective_date || '').localeCompare(a.effective_date || '');
    });
    return copy;
  }, [rows, sortBy]);

  if (isForbidden) {
    return <div className="p-8 text-center" style={{ color: C.soft }} data-testid="rate-editor-forbidden">
      Rate editor is restricted to Super Users.
    </div>;
  }

  return (
    <div data-testid="rate-editor-page" style={{ background: C.bg, minHeight: 'calc(100vh - 4rem)', margin: '-1rem -1rem -2rem -1rem', padding: '1.5rem 2rem 2.5rem' }}>
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: C.gold }}>
            <Crown size={12} /> Contract Rates
          </div>
          <h1 className="font-serif mt-1.5 leading-none" style={{ color: C.ink, fontSize: 'clamp(28px, 4vw, 40px)' }}>
            Payer Rate Editor
          </h1>
          <div className="text-[12px] mt-2 max-w-3xl" style={{ color: C.soft }}>
            When a payer raises or lowers a rate, add a <b>new row</b> with the effective date of the change (use "Duplicate") —
            don't overwrite the old row. Historical months keep the old rate; new visits use the new. The lookup engine
            picks the most recent row whose effective date is on or before the DOS.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/executive-bi" className="inline-flex items-center gap-1 text-[12px] px-3 h-9 rounded-full border transition hover:bg-white" style={{ borderColor: C.border, color: C.ink2 }} data-testid="rate-back-bi">
            <ChevronLeft size={13} /> Back to BI
          </Link>
          <Button data-testid="rate-reload-btn" variant="ghost" onClick={load} disabled={loading || saving}
                  className="h-9 rounded-full">
            <RefreshCcw size={13} className={loading ? 'animate-spin mr-1' : 'mr-1'} /> Reload
          </Button>
          <Button data-testid="rate-add-btn" onClick={addRow}
                  className="h-9 rounded-full border-0" style={{ background: C.purple, color: 'white' }}>
            <Plus size={13} className="mr-1" /> New rate row
          </Button>
          <Button data-testid="rate-save-btn" onClick={save} disabled={!dirty || saving}
                  className="h-9 rounded-full border-0" style={{ background: dirty ? C.ink : C.soft, color: C.gold }}>
            <Save size={13} className="mr-1" /> {saving ? 'Saving…' : dirty ? `Save ${rows.length} rows` : 'Saved'}
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <Card className="rounded-2xl border p-4 mb-4 flex items-start gap-3" style={{ background: '#F7EFDA', borderColor: '#B08A3A' }} data-testid="rate-editor-info">
        <Info size={16} className="shrink-0 mt-0.5" style={{ color: C.gold }} />
        <div className="text-[12px] leading-relaxed" style={{ color: C.ink }}>
          <b>How lookup works:</b> keywords are matched (case-insensitive substring) against the SP raw payer string
          (e.g. "BCBS TXBLS" matches keyword <code>BCBS</code>). Codes: <b>92523</b> → r523 (eval),
          <b> 92609</b> → r609 (AAC), everything else falls back to <b>r507</b> (treatment).
          Rows with the <em>isDefault</em>/<em>isBlankPayer</em> flags handle unmatched and blank payers.
        </div>
      </Card>

      {/* Sort control + summary */}
      <div className="flex items-center justify-between mb-3 text-[12px]" style={{ color: C.soft }}>
        <div><b style={{ color: C.ink }}>{rows.length}</b> rate rows · <b style={{ color: C.teal }}>{latestKeyByPayer.size}</b> unique payers currently active</div>
        <div className="flex items-center gap-2">
          <ArrowUpDown size={12} />
          <select data-testid="rate-sort" value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="text-[12px] border rounded-full px-2 h-7 bg-white" style={{ borderColor: C.border }}>
            <option value="payer_asc">Payer (A→Z)</option>
            <option value="eff_desc">Effective date (newest first)</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <Card className="rounded-2xl overflow-hidden bg-white border" style={{ borderColor: C.border }}>
        <div className="overflow-auto">
          <table className="w-full text-[13px]" data-testid="rate-editor-table">
            <thead className="text-[10px] uppercase tracking-wider" style={{ background: C.hair, color: C.soft }}>
              <tr>
                <th className="text-left py-3 px-3">Payer · Keywords</th>
                <th className="text-left py-3 px-3">Effective</th>
                <th className="text-right py-3 px-2">92523 · Eval</th>
                <th className="text-right py-3 px-2">92507 · Tx</th>
                <th className="text-right py-3 px-2">92609 · AAC</th>
                <th className="text-left py-3 px-3">Notes</th>
                <th className="text-right py-3 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(row => (
                <RateRow
                  key={row._key}
                  row={row}
                  isLatestForPayer={latestKeyByPayer.has(row._key)}
                  onChange={updateRow}
                  onDelete={deleteRow}
                  onDuplicate={duplicateRow}
                />
              ))}
              {!loading && sortedRows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8" style={{ color: C.soft }}>
                  No rate rows yet — click "New rate row" to add one.
                </td></tr>
              )}
              {loading && (
                <tr><td colSpan={7} className="text-center py-8" style={{ color: C.soft }}>Loading rates…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Footer helper */}
      <div className="text-[11px] mt-4 text-center" style={{ color: C.soft }}>
        Changes are applied immediately after saving — the BI cube is refreshed so the new rates flow into
        expected/realization/contract-gap and underpayment detection.
      </div>
    </div>
  );
}
